import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Toast alerts, stacked at the bottom-right (above the floating Go Live bar on phones).
// Fed by showMsg (useSessionState): each toast closes by itself - errors stay a little
// longer - can be dismissed with its ×, and its timer pauses while hovered so it can be
// read. Above every modal, so a failure behind a locked modal is still seen.
// type "success" = green; anything else is an error (red), as the old message banner did.
// Sticky toasts (setStickyToast) never close by themselves and have no × - they go only
// when their owner clears them.

const SUCCESS_MS = 4000;
const ERROR_MS = 6000;

function Toast({ toast, onDismiss, sticky = false }) {
  const { id, text, type } = toast;
  const isError = type !== "success";
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (sticky || paused) return undefined;
    const timer = setTimeout(() => onDismiss(id), isError ? ERROR_MS : SUCCESS_MS);
    return () => clearTimeout(timer);
  }, [id, isError, paused, onDismiss, sticky]);

  return (
    <div
      role={isError ? "alert" : "status"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className={`animate-toast-in pointer-events-auto flex w-full sm:w-80 items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-lg ${
        isError ? "border-red-200" : "border-emerald-200"
      }`}
    >
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white ${
          isError ? "bg-red-500" : "bg-emerald-500"
        }`}
        aria-hidden="true"
      >
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d={isError ? "M12 7v6m0 4h.01" : "M5 13l4 4L19 7"} />
        </svg>
      </span>
      <p className="min-w-0 flex-1 break-words text-sm font-medium text-gray-800">{text}</p>
      {!sticky && (
        <button
          type="button"
          onClick={() => onDismiss(id)}
          aria-label="Dismiss"
          className="-mr-1 -mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

const noop = () => {};

// toasts: [{ id, text, type }] oldest first (newest at the bottom, nearest the corner).
// stickyToasts: the same shape, drawn below them - always in the corner, never moved by
// the toasts coming and going above.
export function Toaster({ toasts = [], stickyToasts = [], onDismiss = noop }) {
  return createPortal(
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-3 bottom-24 z-[120] flex flex-col items-stretch gap-2 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:items-end"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
      {stickyToasts.map((toast) => (
        <Toast key={`sticky-${toast.id}`} toast={toast} onDismiss={onDismiss} sticky />
      ))}
    </div>,
    document.body,
  );
}

export default Toaster;
